/**
 * Paper's density budget — `FE-04.12`.
 *
 * **What this is for.** `FE-13.2` inherits a number rather than a surprise, and
 * `FE-04.7`'s deferral of virtualisation rides on that number being written
 * down where it can be argued with. `docs/notes/density-budget.md` is the prose;
 * this is the arithmetic, so the two cannot drift.
 *
 * ── The method, stated plainly ────────────────────────────────────────────
 * This is **derived, not measured in a browser**, and the distinction is the
 * whole reason the inputs below are separated into two groups. The card front
 * is `FE-12.1`'s and does not exist yet: there is nothing to put in front of a
 * ruler. What does exist is the token layer and the primitives this chunk
 * shipped, so every SETTLED input below is read off a real declaration, and
 * every PROVISIONAL one is named as a thing `FE-12.1` will confirm or move.
 *
 * `implementation-plan.md` marks `FE-04.12` *unit only — documented
 * measurement*, which is this shape: a derivation whose inputs are auditable
 * and whose output is pinned.
 *
 * ⚠ **What is deliberately NOT measured from**: the mock board in
 * `docs/design/stage2/finding-things.html`. It draws 216px columns and a 6px
 * radius against the real 292px `--spacing-list` and 10px `--radius-card` —
 * a schematic at reduced scale, not a specimen. Reading numbers off it would
 * produce a budget roughly 35% too optimistic, and nothing would say so.
 *
 * ── Re-measure when ───────────────────────────────────────────────────────
 * ⚠ **This said "`FE-12.1` ships the real card front", and `FE-12.1` is the
 * card *composer*. The card front shipped in `C1`, under no numbered item of
 * its own** — `apps/web/src/components/board/CardTile.tsx`, `FE-13.2`. So the
 * trigger as written would never have fired, and both this file and
 * `implementation-plan.md`'s `FE-04.12` line named the wrong item. Corrected in
 * place rather than deleted, because the shape of the error is the useful part:
 * a re-measure trigger naming an item that does not own the thing to be
 * measured is indistinguishable from one that has not fired yet.
 *
 * ✅ **It fired, in `C1`, and every input below is now read off a shipped
 * class list.** `PROVISIONAL` is down to the one thing that genuinely has not
 * shipped — the Phase 2 badge row — and the reconciliation is written up in
 * `docs/notes/density-budget.md`. The headline survived at **24**, but *not*
 * because the derivation was right: it was wrong twice, in opposite directions,
 * by almost exactly the same amount. See `cardHeight` below.
 *
 * The next real re-measure is a Playwright measurement against a rendered
 * board, which needs a browser and is not this file's business.
 */

/** Everything here is read off a shipped declaration. */
export const SETTLED = {
  /** `--spacing-list: 18.25rem` in `styles.css`. */
  columnWidth: 292,
  /** `ListPanel`'s `p-2`. */
  panelPadding: 8,
  /** `--text-card: 0.9063rem`. */
  cardText: 14.5,
  /** `LabelChip` `size='sm'` is `h-5`. */
  labelChipHeight: 20,
  /** Paper's stated card gap — `docs/notes/fe-plan.md`, "Two known costs". */
  cardGap: 10,
  /** The board's inter-column gap and its outer padding, from the Stage 2 board frame. */
  columnGap: 12,
  boardPadding: 14,
  /**
   * `AvatarStack size='xs'` is `size-5` — `C1`'s card front (`CardTile.tsx`).
   *
   * ⚠ **The term the original derivation did not have at all**, and the reason
   * it was 20px short: `implementation-plan.md`'s B13 note says labels and
   * member avatars are the *whole* visual vocabulary of a Phase 1 card front,
   * and this file modelled the labels and not the avatars.
   *
   * ⚠ **It is also the tightest number in the budget.** At `size='sm'` (28px,
   * the `AvatarStack` default) the labelled card is 115.2px against a
   * 116.3px threshold — 1.1px from dropping the board from 24 cards to 20. The
   * card front's `size='xs'` is therefore load-bearing rather than cosmetic,
   * and this comment is where a future change to it should be argued.
   */
  avatarRowHeight: 20,
  /** Card front vertical padding per edge — `CardTile`'s `p-2`. */
  cardPaddingY: 8,
  /** `CardTile`'s `leading-[1.35]`. */
  lineHeight: 1.35,
  /** `CardTile`'s `gap-1.5` between its label, title and avatar rows. */
  cardRowGap: 6,
  /** `BoardFace`'s header: `py-3` either side of a `text-xl` line box. */
  boardBarHeight: 52,
  /** `ListColumn`'s header: a `text-sm` line box plus the panel's lower padding. */
  panelHeaderHeight: 28,
  /** The add-card affordance at the foot of every list — `Button size='sm'`, `h-7`. */
  addCardHeight: 28,
} as const;

/**
 * What has still not shipped, and is therefore still an assumption.
 *
 * ⚠ **This group had seven members and now has one.** The other six were
 * `C1`'s to confirm — `cardPaddingY`, `lineHeight`, `cardRowGap`,
 * `boardBarHeight`, `panelHeaderHeight` and `addCardHeight` — and all six moved
 * into `SETTLED` above once there was a class list to read them off. Four were
 * right as assumed; `boardBarHeight` was 48 and is 52; `addCardHeight` was 32
 * and is 28 (`Button size='sm'` is `h-7`). Kept as a *group* rather than
 * flattened into one list, because the distinction it encodes — a number read
 * off a declaration versus a number somebody reasoned to — is the whole reason
 * this budget can be argued with.
 */
export const PROVISIONAL = {
  /**
   * The card-front badge/meta row (`FR-CARD-034`'s counters) — **Phase 2, B25**.
   *
   * ⚠ It is no longer part of `cardHeight`, and removing it is what made the
   * budget honest: `C1`'s card front renders no badges at all, so charging 16px
   * for them was a phantom cost. The constant stays because `B25` will make it
   * real, and because deleting it would erase the record of the error.
   */
  badgeRowHeight: 16,
} as const;

/** The three card fronts a real board actually contains, tallest first. */
export type CardShape = 'labelled' | 'plain' | 'minimal';

/**
 * Height of one card front, in CSS pixels.
 *
 * `labelled` is the honest default for a board that uses labels at all —
 * `FR-CARD-009`'s acceptance criteria put labels on the card front, and Paper
 * renders them as **full pills carrying the name**, so they take a row rather
 * than a stripe. That choice is what this budget mostly costs.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE RECONCILIATION, AND WHY "IT CAME OUT AT 24 BOTH TIMES" IS NOT REASSURING
 * ---------------------------------------------------------------------------
 * The original `labelled` height was **103.2px**; the shipped card front is
 * **107.2px**. Four pixels apart, and both give six rows at 900px — so every
 * assertion in `density.test.ts` passed before and after, and the headline is
 * 24 either way.
 *
 * It was wrong **twice**, in opposite directions, by almost the same amount:
 *
 *   − it charged **16px** for a badge row `C1`'s card front does not render
 *     (Phase 2, `B25`);
 *   + it charged **0px** for the member avatar row, which B13's own scope note
 *     names as half of the card front's entire visual vocabulary.
 *
 * Two errors of 16 and 20 cancelling to 4 is the failure mode a derived budget
 * has and a measured one does not, and it is exactly why `FE-04.12` was
 * required to say *provisional* out loud rather than just publish the number.
 * The number was never the deliverable; the auditability was.
 *
 * `plain` and `minimal` are unchanged and were right — they had no avatar row
 * and no badge row to get wrong.
 */
export function cardHeight(shape: CardShape): number {
  const line = Math.round(SETTLED.cardText * SETTLED.lineHeight * 10) / 10;
  const padding = SETTLED.cardPaddingY * 2;

  switch (shape) {
    case 'labelled':
      return (
        padding +
        SETTLED.labelChipHeight +
        SETTLED.cardRowGap +
        line * 2 +
        SETTLED.cardRowGap +
        SETTLED.avatarRowHeight
      );
    case 'plain':
      return padding + line * 2;
    case 'minimal':
      return padding + line;
  }
}

/**
 * Board chrome above and below a list's scrollable card area.
 *
 * ⚠ **Also reconciled against `C1`, and also in the forgiving direction.** The
 * shipped board bar is 52px rather than 48, and the shipped add-card button is
 * 28px rather than 32 — but `BoardFace`'s strip carries **no top padding at
 * all** (the header's own `py-3` is the whole gap), where this charged a
 * `boardPadding` above the panel. Net: the real chrome is **10px shorter above
 * and 6px shorter below** than budgeted, so the 24 is conservative rather than
 * optimistic. `boardPadding` is left in the sum below and set against the
 * strip's actual `pb-3`; the horizontal use in `columnsPerViewport` is
 * unaffected.
 */
export function chromeHeight(): { above: number; below: number } {
  return {
    above: SETTLED.boardBarHeight + SETTLED.panelPadding + SETTLED.panelHeaderHeight,
    below: SETTLED.panelPadding + SETTLED.boardPadding + SETTLED.addCardHeight,
  };
}

/**
 * Whole cards visible in one list at a given viewport height.
 *
 * The trailing `+ cardGap` is not a fudge: the last card in view needs no gap
 * beneath it, so a strict `height / (card + gap)` under-counts by one whenever
 * the remainder happens to clear the card but not the gap.
 */
export function cardsPerViewport(
  viewportHeight: number,
  shape: CardShape = 'labelled',
): number {
  const { above, below } = chromeHeight();
  const available = viewportHeight - above - below;
  if (available <= 0) return 0;
  return Math.max(
    0,
    Math.floor((available + SETTLED.cardGap) / (cardHeight(shape) + SETTLED.cardGap)),
  );
}

/** Whole 292px columns visible at a given viewport width. */
export function columnsPerViewport(viewportWidth: number): number {
  const usable = viewportWidth - SETTLED.boardPadding * 2 + SETTLED.columnGap;
  return Math.max(0, Math.floor(usable / (SETTLED.columnWidth + SETTLED.columnGap)));
}

/**
 * The headline number, and the one `FE-13.2` should quote.
 *
 * Cards on screen at once across the whole board face — columns × cards per
 * column — which is the figure the ten-second rule is actually about.
 */
export function cardsOnScreen(
  viewportWidth: number,
  viewportHeight: number,
  shape: CardShape = 'labelled',
): number {
  return columnsPerViewport(viewportWidth) * cardsPerViewport(viewportHeight, shape);
}

/**
 * How many viewport-heights of scrolling a full list costs.
 *
 * `04-views.md` asserts a **200-card list** stays legible while scrolling, and
 * `FR-BOARD-035` caps a board at 5,000 open cards. This is the number that
 * decides whether `FE-04.7`'s deferral of virtualisation was the right call —
 * and it is a scroll-distance argument, not a rendering-cost one.
 */
export function screensToScroll(
  cardCount: number,
  viewportHeight: number,
  shape: CardShape = 'labelled',
): number {
  const perScreen = cardsPerViewport(viewportHeight, shape);
  if (perScreen === 0) return Infinity;
  return Math.round((cardCount / perScreen) * 10) / 10;
}
